<template>
  <DModal :show-close="false" :isBgOverlay="true" :isCloseOnBgClick="false" @close="$emit('close')" :appendToBody="true"
          style="z-index: 12312312312312" :placeInTop="true">
    <div class="d-modal" slot="insertComponent">
      <div class="d-modal__header">
        <h2>Opret fraværsindberetning</h2>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="d-modal__content">
        <div class="d-input-group">
          <label class="d-label">Vælg ansat *</label>
          <DSelect
            ref="userSelect"
            :items="filteredUsers"
            :multiple="false"
            :placeholder="'Vælg ansat'"
            :itemsSelected="itemsSelectedForDSelect"
            :isShowRemoveButton="false"
            @add="onSelectUser"
            @remove="onSelectUser(null)"
          />
        </div>
        <div class="d-input-group">
          <label class="d-label">Ansættelse *</label>
          <select v-model="employmentId" class="height-34 do-select width-100"
                  :disabled="!userId || !hasEmploymentsWithNumber">
            <option v-for="employment in employments" :key="employment.id" :value="employment.id">
              {{ employment.title }} - {{ employment.employment_number }}
            </option>
          </select>
          <p v-if="userId && !hasEmploymentsWithNumber" class="text-explainer marg-05-top">
            Den valgte bruger har ingen ansættelser med tjenestenummer
          </p>
        </div>
        <div class="d-input-group">
          <label class="d-label">Fraværstype *</label>
          <select v-model="absenceType" class="height-34 do-select width-100">
            <option v-for="type in absenceTypes" :key="type.value" :value="type.value">
              {{ type.label }}
            </option>
          </select>
        </div>
        <div class="d-input-group-flex">
          <div class="d-input-group">
            <label class="d-label">Startdato *</label>
            <DateSelector class="width-100" :date="startDate" :width100="true" @changed="setStartDate($event)"/>
          </div>
          <div class="d-input-group">
            <label class="d-label">Slutdato</label>
            <DateSelector class="width-100" :date="endDate" :width100="true" @changed="setEndDate($event)"/>
          </div>
        </div>

        <p v-if="validationMessage" class="text-explainer" style="margin-top: 8px">{{ validationMessage }}</p>
      </div>

      <div class="d-modal__buttons">
        <div class="d-modal__buttons__child">
          <button class="do-bttn" @click="$emit('close')">{{ $t('vue2.common.cancel') }}</button>
          <button class="do-bttn do-bttn--submit"
                  :class="{'do-bttn--submit--loading': isSubmitting, 'do-bttn--submit--disabled': !isValid}"
                  :disabled="!isValid || isSubmitting"
                  @click="submit"
                  :content="saveKeyTooltip"
                  v-tippy="{
                    arrow: true,
                      placement : 'right',
                      animateFill: false,
                      animation : 'shift-away',
                      boundary: 'window',
                      zIndex: 12312312312312
                    }">
            Send til OPUS
          </button>
        </div>
      </div>
    </div>
  </DModal>
</template>

<script>
import DModal from '../../common/DModal.vue'
import DateSelector from '../../datepicker/DateSelector.vue'
import moment from 'moment'
import eventbus from '../../../eventbus'
import docendo from '../../../docendo'
import DSelect from '../../DSelect/DSelect.vue'

export default {
  components: {DSelect, DModal, DateSelector},
  props: {
    schoolId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      userId: '',
      employmentId: null,
      startDate: moment(),
      endDate: moment().add(1, 'day'),
      absenceType: 'SY',
      isSubmitting: false,
      validationMessage: ''
    }
  },
  computed: {
    metaKey () {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      return isMac ? '⌘' : 'CTRL'
    },
    saveKeyTooltip () {
      return '<p class="tooltip-shortcut tooltip-shortcut--double-key"><span>' + this.metaKey + '</span>' + '<span>Enter</span></p>'
    },
    isValid () {
      const hasUser = !!this.userId
      const hasEmployment = !!this.employmentId
      const hasAbsenceType = !!this.absenceType && String(this.absenceType).trim() !== ''
      const hasStartDate = !!this.startDate && moment(this.startDate).isValid()
      const emp = this.selectedEmployment
      const hasEmploymentNumbers = emp && emp.employment_number

      return hasUser && hasEmployment && hasAbsenceType && hasStartDate && hasEmploymentNumbers
    },
    usersSortedOnName () {
      return this.$store.getters.usersSortedOnName
    },
    filteredUsers () {
      return this.usersSortedOnName.filter(user => this.employmentsInUserIdHash[user.id])
    },
    itemsSelectedForDSelect () {
      return this.selectedUser ? [this.selectedUser] : []
    },
    selectedUser () {
      return this.usersSortedOnName.find(u => u.id === this.userId)
    },
    employmentsInUserIdHash () {
      return this.$store.getters.employmentsInUserIdHash
    },
    employmentsRaw () {
      const has = this.userId && this.employmentsInUserIdHash[this.userId]
      return has ? this.employmentsInUserIdHash[this.userId] : []
    },
    employments () {
      const src = this.employmentsRaw
      const out = []
      for (let i = 0; i < src.length; i++) {
        const e = src[i]
        const num = e && e.employment_number ? String(e.employment_number) : ''
        if (num.trim() !== '') out.push(e)
      }
      return out
    },
    hasEmploymentsWithNumber () {
      return this.userId && this.employments.length > 0
    },
    selectedEmployment () {
      const list = this.employments
      for (let i = 0; i < list.length; i++) {
        if (list[i].id === this.employmentId) return list[i]
      }
      return null
    },
    absenceTypes () {
      return [
        {value: 'SY', label: 'SY - Sygdom'},
        {value: 'FE', label: 'FE - Ferie'},
        {value: 'BE', label: 'BE - Barsel'},
        {value: 'OR', label: 'OR - Orlov'},
        {value: 'FR', label: 'FR - Fritagelse'},
        {value: 'ST', label: 'ST - Sygdom med løn'},
        {value: 'SU', label: 'SU - Sygdom uden løn'},
        {value: 'FU', label: 'FU - Fritagelse uden løn'},
        {value: 'BU', label: 'BU - Barsel uden løn'}
      ]
    }
  },
  watch: {
    userId () {
      this.onUserChange()
    }
  },
  methods: {
    onSelectUser (item) {
      if (!item) {
        this.userId = ''
      } else {
        this.userId = item.id
      }
      // Auto-select employment if user only has one
      if (item && item.id) {
        this.$nextTick(() => {
          if (this.employments && this.employments.length === 1) {
            this.employmentId = this.employments[0].id
          }
        })
      }
    },
    focusUserSelect () {
      const el = this.$refs && this.$refs.userSelect ? this.$refs.userSelect : null
      if (!el) return
      el.open()
    },
    setStartDate (date) {
      this.startDate = date
    },
    setEndDate (date) {
      this.endDate = date
    },
    onGlobalKeydown (evt) {
      // Cmd+Enter (mac) eller Ctrl+Enter (win/linux)
      const isSubmitCombo = (evt.metaKey || evt.ctrlKey) && (evt.key === 'Enter' || evt.keyCode === 13)
      if (!isSubmitCombo) return

      // respektér disable-tilstand
      if (this.isSubmitting || !this.isValid) return

      evt.preventDefault()
      this.submit()
    },
    onUserChange () {
      this.employmentId = null
    },
    submit () {
      this.validationMessage = ''
      if (!this.isValid) {
        this.validationMessage = 'Udfyld alle obligatoriske felter'
        return
      }
      if (!this.schoolId) {
        this.validationMessage = 'Mangler schoolId'
        return
      }

      const startDateStr = moment(this.startDate).format('YYYY-MM-DD')
      const endDateStr = this.endDate && moment(this.endDate).isValid()
        ? moment(this.endDate).format('YYYY-MM-DD')
        : '9999-12-31'

      const success = res => {
        this.isSubmitting = false
        eventbus.toast = {text: 'Fraværsindberetning oprettet', success: true, position: 'bottomLeft'}
        this.$emit('created', res && res ? res : null)
        this.$emit('close')
      }
      const failure = err => {
        console.log('failure', err)
        this.isSubmitting = false
        eventbus.toast = {text: 'Kunne ikke oprette fraværsindberetning', success: false, position: 'bottomLeft'}
      }

      this.isSubmitting = true
      docendo.createKmdOpusAbsence(
        this.schoolId,
        this.employmentId,
        startDateStr,
        endDateStr,
        this.absenceType,
        success,
        failure
      )
    }
  },
  mounted () {
    this.$store.dispatch('readEmployments')
    window.addEventListener('keydown', this.onGlobalKeydown)
    // giv fokus når modalen er i DOM
    this.$nextTick(() => {
      setTimeout(() => {
        this.focusUserSelect()
      }, 500)
    })
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onGlobalKeydown)
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.d-modal {
  width: 600px;
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
  text-align: left;
}

.d-modal__buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem 2rem;
  border-top: 1px solid #eee;
  margin: 0;
}

.d-modal__buttons__child {
  display: flex;
  gap: 0.5rem;
}

.d-modal__content {
  padding: 1rem;
  overscroll-behavior: contain;
}

.d-input-group .d-input {
  height: 34px;
}

.d-input-group {
  margin-bottom: 1rem;
}

.d-modal__content {
  max-height: calc(100vh - 52px - 80px);
  overflow-y: auto;
}

.height-34 {
  height: 34px;
  margin-top: 0.25rem;
}

.do-select[disabled] {
  background: #eee;
  cursor: not-allowed;
}

.d-input-group-flex {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.d-input-group-flex > div {
  flex: 1;
}

.d-input-group-flex > p {
  color: #ccc;
  font-style: italic;
  margin-top: 1rem;
}

</style>



// WEBPACK FOOTER //
// CreateOpusAbsence.vue?a48e55b0